import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import {
  Grid,
  MenuItem,
  ListItemText,
  Button,
  FormControl,
  InputLabel,
  Input,
  Checkbox,
  Select,
} from '@mui/material';
import { AnchorPopup } from 'shared/components';
import { Cancel, Delete, ToggleOn } from '@mui/icons-material/';
import { setFilters, applyFilters } from '../../actions/filters';
import { CUBE_FILTERS } from '../../utils/constants';

const useStyles = makeStyles(() => ({
  rootBottom: {
    flexGrow: 1,
    marginTop: 10,
    marginBottom: 10,
    textAlign: 'center',
  },
  form: {
    width: '100%',
  },
  select: {
    display: 'block',
    width: '100%',
  },
  button: {
    fontSize: 12,
  },
}));

const FiltersPopup = ({ filters, ...props }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [selectedPortfolios, setSelectedPortfolios] = React.useState([]);
  const [selectedYears, setSelectedYears] = React.useState([]);
  const [selectedManagers, setSelectedManagers] = React.useState([]);
  const [selectedSocialImpact, setSelectedSocialImpact] = React.useState([]);
  const { portfolios, years, managers, indicators } = filters;
  const { selectedFilters } = useSelector(state => state.filters);

  React.useEffect(() => {
    dispatch(
      setFilters({
        siIndicators: selectedSocialImpact,
        portfolioIds: selectedPortfolios.map(item => item.id),
        relationshipMgrs: selectedManagers.map(item => item.id),
        portfolioYears: selectedYears,
      }),
    );
  }, [
    selectedPortfolios,
    selectedYears,
    selectedManagers,
    selectedSocialImpact,
    dispatch,
  ]);

  const handleChangePortfolio = event => {
    setSelectedPortfolios(event.target.value);
  };

  const handleChangeYear = event => {
    setSelectedYears(event.target.value);
  };

  const handleChangeManager = event => {
    setSelectedManagers(event.target.value);
  };

  const handleChangeSocialImpact = event => {
    setSelectedSocialImpact(event.target.value);
  };

  const handleClear = () => {
    setSelectedPortfolios([]);
    setSelectedYears([]);
    setSelectedManagers([]);
    setSelectedSocialImpact([]);
    dispatch(applyFilters([]));
  };

  const canApplyFilters = () => {
    const selected = Object.keys(selectedFilters).reduce((total, key) => {
      total += selectedFilters[key].length;
      return total;
    }, 0);
    return selected > 0;
  };

  const getCubeFilter = (dimension, values) => {
    return {
      dimension,
      operator: 'equals',
      values,
    };
  };

  const handleApply = () => {
    const cubeFilters = [];
    const {
      portfolioIds,
      portfolioYears,
      relationshipMgrs,
      siIndicators,
    } = selectedFilters;
    if (portfolioIds.length > 0) {
      cubeFilters.push(getCubeFilter(CUBE_FILTERS.PORTFOLIOS_ID, portfolioIds));
    }
    if (portfolioYears.length > 0) {
      cubeFilters.push(
        getCubeFilter(CUBE_FILTERS.PORTFOLIOS_YEAR, portfolioYears),
      );
    }
    if (relationshipMgrs.length > 0) {
      cubeFilters.push(getCubeFilter(CUBE_FILTERS.USERS_ID, relationshipMgrs));
    }
    if (siIndicators.length > 0) {
      cubeFilters.push(
        getCubeFilter(CUBE_FILTERS.INDICATORS_NAME, siIndicators),
      );
    }
    dispatch(applyFilters(cubeFilters));
  };

  return (
    <AnchorPopup {...props}>
      <FormControl className={classes.form}>
        <InputLabel>Portfolio</InputLabel>
        <Select
          className={classes.select}
          multiple
          value={selectedPortfolios}
          onChange={handleChangePortfolio}
          renderValue={selected => selected.map(item => item.name).join(', ')}
          input={<Input />}
        >
          {portfolios.map(item => (
            <MenuItem key={item.id} value={item}>
              <Checkbox checked={selectedPortfolios.indexOf(item) > -1} />
              <ListItemText primary={item.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl className={classes.form}>
        <InputLabel>Year</InputLabel>
        <Select
          className={classes.select}
          multiple
          value={selectedYears}
          onChange={handleChangeYear}
          renderValue={selected => selected.join(', ')}
          input={<Input />}
        >
          {years.map(name => (
            <MenuItem key={name} value={name}>
              <Checkbox checked={selectedYears.indexOf(name) > -1} />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl className={classes.form}>
        <InputLabel>Manager</InputLabel>
        <Select
          className={classes.select}
          multiple
          value={selectedManagers}
          onChange={handleChangeManager}
          renderValue={selected => selected.map(item => item.name).join(', ')}
          input={<Input />}
        >
          {managers.map(item => (
            <MenuItem key={item.id} value={item}>
              <Checkbox checked={selectedManagers.indexOf(item) > -1} />
              <ListItemText primary={item.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl className={classes.form}>
        <InputLabel>Social Impact Indicator</InputLabel>
        <Select
          className={classes.select}
          multiple
          value={selectedSocialImpact}
          onChange={handleChangeSocialImpact}
          renderValue={selected => selected.join(', ')}
          input={<Input />}
        >
          {indicators.map(name => (
            <MenuItem key={name} value={name}>
              <Checkbox checked={selectedSocialImpact.indexOf(name) > -1} />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className={classes.rootBottom}>
        <Grid container direction="row" justify="center" alignItems="center">
          <Grid item xs={4}>
            <Button
              className={classes.button}
              variant="contained"
              color="inherit"
              startIcon={<Cancel />}
              onClick={props.onClose}
            >
              Close
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              className={classes.button}
              variant="contained"
              color="inherit"
              startIcon={<Delete />}
              onClick={handleClear}
            >
              Clear
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              disabled={!canApplyFilters()}
              className={classes.button}
              variant="contained"
              color="primary"
              startIcon={<ToggleOn />}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      </div>
    </AnchorPopup>
  );
};

FiltersPopup.propTypes = {
  filters: PropTypes.object,
  onClose: PropTypes.func,
};

export default FiltersPopup;
